import { useState } from "react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import axios from "axios";
import { Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function PinLogin({ onLogin }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (pin.length !== 6) {
      toast.error("Please enter a 6-digit PIN");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-pin`, { pin });
      if (response.data.success) {
        toast.success("Access granted");
        onLogin();
      }
    } catch (error) {
      toast.error("Invalid PIN");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && pin.length === 6) {
      handleVerify();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] px-4">
      <div 
        className="w-full max-w-md animate-fadeIn"
        data-testid="pin-login-container"
      >
        {/* Logo Section */}
        <div className="text-center mb-10 pt-6">
          <div className="inline-flex items-center justify-center w-28 h-28 sm:w-36 sm:h-36 rounded-sm bg-transparent mb-6 mt-4">
            <img 
              src="./assets/logo.png" 
              alt="Loong Fu Logo" 
              className="w-24 h-24 sm:w-32 sm:h-32 object-contain"
              data-testid="login-logo"
            />
          </div>
          <p className="text-[#D4AF37] text-4xl sm:text-5xl tracking-[0.25em] mb-4" style={{ fontFamily: 'serif' }}>
            龍 虎 堂
          </p>
          <h1 
            className="text-2xl sm:text-3xl font-bold tracking-widest text-[#D4AF37]"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            LOONG FU TONG
          </h1>
        </div>

        {/* PIN Entry Card */}
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-8">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="w-5 h-5 text-[#A1A1AA]" />
            <h2 
              className="text-lg font-semibold text-white uppercase tracking-wide"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Enter PIN
            </h2>
          </div>

          <div className="flex justify-center mb-8" onKeyDown={handleKeyDown}>
            <InputOTP
              maxLength={6}
              value={pin}
              onChange={setPin}
              data-testid="pin-input"
            >
              <InputOTPGroup className="gap-2">
                <InputOTPSlot 
                  index={0} 
                  className="w-11 h-14 text-2xl bg-[#27272a] border-white/10 text-white rounded-sm"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                />
                <InputOTPSlot 
                  index={1} 
                  className="w-11 h-14 text-2xl bg-[#27272a] border-white/10 text-white rounded-sm"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                />
                <InputOTPSlot 
                  index={2} 
                  className="w-11 h-14 text-2xl bg-[#27272a] border-white/10 text-white rounded-sm"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                />
                <InputOTPSlot 
                  index={3} 
                  className="w-11 h-14 text-2xl bg-[#27272a] border-white/10 text-white rounded-sm"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                />
                <InputOTPSlot 
                  index={4} 
                  className="w-11 h-14 text-2xl bg-[#27272a] border-white/10 text-white rounded-sm"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                />
                <InputOTPSlot 
                  index={5} 
                  className="w-11 h-14 text-2xl bg-[#27272a] border-white/10 text-white rounded-sm"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <Button
            data-testid="verify-pin-btn"
            onClick={handleVerify}
            disabled={loading || pin.length !== 6}
            className="w-full h-12 bg-[#D92323] hover:bg-[#b91c1c] text-white font-semibold uppercase tracking-wider rounded-sm transition-all hover:-translate-y-0.5"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            {loading ? "Verifying..." : "Enter The Dragon"}
          </Button>
        </div>
      </div>
    </div>
  );
}
